import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useDebounce } from '@/hooks/useDebounce'
import { useAttributesStore } from '@/stores/useAttributesStore'
import { AttributesFilters } from '@/components/Attributes/AttributesFilters'
import { AttributesGrid } from '@/components/Attributes/AttributesGrid'
import { AttributeFormDialog } from '@/components/Attributes/AttributeFormDialog'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { toast } from '@/stores/useToastStore'
import {
  useCreateSkuAttribute,
  useDeleteSkuAttribute,
  useSkuAttributeCategories,
  useSkuAttributes,
  useUpdateSkuAttribute,
} from '@/hooks/useSkuAttributes'
import type { Attribute, AttributeFormData } from '@/types/attribute'

export default function AdminAttributes() {
  const { attributes, setAttributes, updateAttribute, deleteAttribute } =
    useAttributesStore()
  const createAttributeMutation = useCreateSkuAttribute()
  const updateAttributeMutation = useUpdateSkuAttribute()
  const deleteAttributeMutation = useDeleteSkuAttribute()
  const { data: apiAttributes, isError: isAttributesLoadError } =
    useSkuAttributes(0, 100)
  const { data: rootCategories = [] } = useSkuAttributeCategories(null)
  const hasShownLoadError = useRef(false)

  useEffect(() => {
    if (apiAttributes) {
      setAttributes(apiAttributes)
    }
  }, [apiAttributes, setAttributes])

  useEffect(() => {
    if (isAttributesLoadError && !hasShownLoadError.current) {
      toast.error('Failed to load attributes')
      hasShownLoadError.current = true
    }

    if (!isAttributesLoadError) {
      hasShownLoadError.current = false
    }
  }, [isAttributesLoadError])

  // Search and filter state
  const [searchInput, setSearchInput] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | 'all'>('all')
  const [subcategoryFilter, setSubcategoryFilter] = useState<string | 'all'>(
    'all'
  )
  const debouncedSearch = useDebounce(searchInput, 300)

  const selectedCategoryId = useMemo(() => {
    if (categoryFilter === 'all') {
      return null
    }

    return (
      rootCategories.find(
        category =>
          category.name.trim().toLowerCase() ===
          categoryFilter.trim().toLowerCase()
      )?.id ?? null
    )
  }, [categoryFilter, rootCategories])

  const { data: dependentSubcategories = [] } = useSkuAttributeCategories(
    selectedCategoryId,
    Boolean(selectedCategoryId)
  )

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute | null>(
    null
  )

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [attributeToDelete, setAttributeToDelete] = useState<Attribute | null>(
    null
  )

  // Filter attributes
  const filteredAttributes = useMemo(() => {
    return attributes
      .filter(a => categoryFilter === 'all' || a.category === categoryFilter)
      .filter(
        a => subcategoryFilter === 'all' || a.subcategory === subcategoryFilter
      )
      .filter(
        a =>
          !debouncedSearch ||
          a.name.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
  }, [attributes, categoryFilter, subcategoryFilter, debouncedSearch])

  const categoryOptions = useMemo(() => {
    const values = new Set<string>()

    rootCategories.forEach(category => {
      const normalized = category.name.trim()
      if (normalized.length > 0) {
        values.add(normalized)
      }
    })

    attributes.forEach(attribute => {
      const normalized = attribute.category.trim()
      if (normalized.length > 0) {
        values.add(normalized)
      }
    })

    return Array.from(values).sort((left, right) => left.localeCompare(right))
  }, [attributes, rootCategories])

  const subcategoryOptions = useMemo(() => {
    const values = new Set<string>()

    if (categoryFilter === 'all') {
      attributes.forEach(attribute => {
        const normalized = attribute.subcategory.trim()
        if (normalized.length > 0) {
          values.add(normalized)
        }
      })

      return Array.from(values).sort((left, right) => left.localeCompare(right))
    }

    if (selectedCategoryId && dependentSubcategories.length > 0) {
      dependentSubcategories.forEach(subcategory => {
        const normalized = subcategory.name.trim()
        if (normalized.length > 0) {
          values.add(normalized)
        }
      })
    }

    attributes
      .filter(attribute => attribute.category === categoryFilter)
      .forEach(attribute => {
        const normalized = attribute.subcategory.trim()
        if (normalized.length > 0) {
          values.add(normalized)
        }
      })

    return Array.from(values).sort((left, right) => left.localeCompare(right))
  }, [attributes, categoryFilter, selectedCategoryId, dependentSubcategories])

  const handleCategoryFilterChange = useCallback((value: string | 'all') => {
    setCategoryFilter(value)
    setSubcategoryFilter('all')
  }, [])

  // Handlers
  const handleAddAttribute = useCallback(() => {
    setSelectedAttribute(null)
    setDialogOpen(true)
  }, [])

  const handleEditAttribute = useCallback((attribute: Attribute) => {
    setSelectedAttribute(attribute)
    setDialogOpen(true)
  }, [])

  const handleDeleteClick = useCallback((attribute: Attribute) => {
    setAttributeToDelete(attribute)
    setDeleteDialogOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (attributeToDelete) {
      try {
        await deleteAttributeMutation.mutateAsync(attributeToDelete.id)
      } catch {
        return
      }

      deleteAttribute(attributeToDelete.id)
      toast.success(`"${attributeToDelete.name}" has been deleted`)
      setDeleteDialogOpen(false)
      setAttributeToDelete(null)
    }
  }, [attributeToDelete, deleteAttribute, deleteAttributeMutation])

  const handleFormSubmit = useCallback(
    async (data: AttributeFormData) => {
      if (selectedAttribute) {
        await updateAttributeMutation.mutateAsync({
          id: selectedAttribute.id,
          data,
        })

        updateAttribute(selectedAttribute.id, data)
        toast.success(`"${data.name}" has been updated`)
      } else {
        await createAttributeMutation.mutateAsync(data)

        toast.success(`"${data.name}" has been added`)
      }
    },
    [
      selectedAttribute,
      updateAttribute,
      updateAttributeMutation,
      createAttributeMutation,
    ]
  )

  return (
    <>
      {/* Attribute Form Dialog */}
      <AttributeFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        attribute={selectedAttribute}
        onSubmit={handleFormSubmit}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Attribute"
        description={`Are you sure you want to delete "${attributeToDelete?.name ?? ''}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        variant="destructive"
      />

      <div className="flex flex-col gap-6 p-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Attribute Utility
            </h1>
            <p className="text-base text-muted-foreground mt-1">
              Add, edit, and manage attribute definitions
            </p>
          </div>
          <Button className="gap-2" onClick={handleAddAttribute}>
            <Plus className="h-4 w-4" />
            Add Attribute
          </Button>
        </div>

        {/* Filter Bar */}
        <AttributesFilters
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={handleCategoryFilterChange}
          subcategoryFilter={subcategoryFilter}
          onSubcategoryFilterChange={setSubcategoryFilter}
          categoryOptions={categoryOptions}
          subcategoryOptions={subcategoryOptions}
        />

        {/* Attributes Grid */}
        <AttributesGrid
          attributes={filteredAttributes}
          onEdit={handleEditAttribute}
          onDelete={handleDeleteClick}
        />
      </div>
    </>
  )
}
